import { syncStorage } from "@/lib/storage";
import {
	ApolloClient,
	ApolloError,
	ApolloLink,
	DefaultOptions,
	InMemoryCache,
	NormalizedCacheObject,
	createHttpLink,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { logger } from "../logger";
import { initializeDigestLogger, logError } from "./digest-logger";

const defaultOptions: DefaultOptions = {
	watchQuery: {
		errorPolicy: "all",
		fetchPolicy: "cache-and-network",
	},
	query: {
		errorPolicy: "all",
		fetchPolicy: "no-cache",
	},
	mutate: {
		errorPolicy: "all",
	},
};

const mockClient = new ApolloClient({
	cache: new InMemoryCache(),
	link: new ApolloLink((operation, forward) => {
		operation.setContext({
			headers: {
				"x-graphql-error": "true",
			},
		});
		return forward(operation);
	}),
	defaultOptions,
});

let client: ApolloClient<NormalizedCacheObject>;
let isConnectionEstablished = false;

export const APOLLO_CONNECTION_TIMEOUT = 30_000; // 30 seconds for initial connection
export const APOLLO_REQUEST_TIMEOUT = 60_000; // 1 minute for regular requests after connection

// Generate or retrieve session ID - only create once per browser session
let sessionId: string | null = null;

const getSessionId = () => {
	// Return cached session ID if already generated
	if (sessionId) return sessionId;

	// Try to get from storage first
	sessionId = syncStorage.getItem("settlin_session_id");

	// Only create new session ID if none exists
	if (!sessionId) {
		// Generate a unique session ID per browser instance (not per tab)
		// This allows connection reuse across tabs while maintaining auth separation between browsers
		sessionId = `web-${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
		syncStorage.setItem("settlin_session_id", sessionId);
	}

	return sessionId;
};

export const getApolloClient = () => {
	if (!client) {
		// Determine GraphQL URI based on environment and proxy settings
		const useProxy = process.env.NODE_ENV !== "production" || process.env.NEXT_PUBLIC_USE_GRAPHQL_PROXY; // default false
		// const useProxy = process.env.NEXT_PUBLIC_USE_GRAPHQL_PROXY !== "false";
		const isClientSide = typeof window !== "undefined";

		let graphqlUri: string;

		if (isClientSide && useProxy) {
			// Use Next.js API route proxy for client-side requests to eliminate CORS issues
			graphqlUri = "/api/graphql";
		} else {
			// Use direct GraphQL endpoint for server-side requests or when proxy is disabled
			const envUri = process.env.NEXT_PUBLIC_GRAPHQL_URL;
			if (!envUri) {
				logger.error("❌ NEXT_PUBLIC_GRAPHQL_URL environment variable is required");
				return mockClient;
			}
			graphqlUri = envUri.trim().replace(/^["']|["']$/g, "");
		}

		// Create HTTP link with dynamic timeout based on connection state
		const httpLink = createHttpLink({
			uri: graphqlUri,
			fetch: (uri, options) => {
				const controller = new AbortController();
				// Use connection timeout for first request, regular timeout for subsequent requests
				const timeout = isConnectionEstablished ? APOLLO_REQUEST_TIMEOUT : APOLLO_CONNECTION_TIMEOUT;
				const timeoutId = setTimeout(() => controller.abort(), timeout);

				return fetch(uri, {
					...options,
					signal: controller.signal,
				})
					.then(response => {
						// Mark connection as established on successful response
						if (response.ok) {
							isConnectionEstablished = true;
						}
						return response;
					})
					.finally(() => {
						clearTimeout(timeoutId);
					});
			},
		});

		// Create error link for handling GraphQL and network errors
		// Note: graphQLErrors and networkError are deprecated in Apollo Client 4.0 but safe to use in 3.x
		// They will be consolidated into a single `error` property in 4.0
		const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
			if (graphQLErrors) {
				// Check for "Invalid group id" error (occurs during backend group merges)
				// Use console.warn instead of logger to prevent log noise
				const isInvalidGroupError = graphQLErrors.some(err => err.message === "Invalid group id");

				if (isInvalidGroupError) {
					// eslint-disable-next-line no-console
					console.warn("GraphQL Error (Ignored from Logger): Invalid group id", {
						operationName: operation.operationName,
						variables: operation.variables,
					});
				} else {
					// Use the enhanced GraphQL error logger
					const apolloError = { graphQLErrors, networkError } as ApolloError;
					logger.graphqlError(apolloError, operation.operationName, operation.variables);
				}
			}

			if (networkError) {
				// Initialize digest logger for aggregate tracking
				initializeDigestLogger();

				// Handle timeout errors specifically
				if (networkError.name === "AbortError") {
					// eslint-disable-next-line no-console
					console.error(`Apollo timeout for ${operation.operationName}: ${networkError.message}`);
					logError("timeout");
				}
				// SecurityError for localStorage/sessionStorage access is expected in restricted contexts
				// (iframes, incognito mode, etc.) - log as warn instead of error
				else if (
					networkError.name === "SecurityError" ||
					networkError.message.includes("Access is denied") ||
					networkError.message.includes("localStorage")
				) {
					logger.warn("Apollo Network Error (Security)", {
						operationName: operation.operationName,
						errorMessage: networkError.message,
					});
					logError("network");
				}
				// Other network errors (fetch failures, etc.) - only track in digest
				// Individual logging will happen after retries are exhausted in get-data.ts
				else if (networkError.message.includes("fetch") || networkError.message.includes("Failed to connect")) {
					logError("connection");
				} else {
					logError("network");
				}
			}
		});

		// Create auth link to add authentication headers
		const authLink = setContext((_, { headers }) => {
			// Get the authentication token from storage
			const token = syncStorage.getItem("settlin_auth_token");

			// Get session ID using the cached function
			const currentSessionId = getSessionId();

			// Return the headers to the context so httpLink can read them
			return {
				headers: {
					...headers,
					authorization: token ? `Bearer ${token} webApp` : "",
					"x-app-id": "webApp",
					// Send both headers for backwards compatibility
					"x-session-id": currentSessionId,
				},
			};
		});

		// the default fetch policy is no-cache
		// if you want to use cache, you need to set it explicitly
		client = new ApolloClient({
			link: errorLink.concat(authLink.concat(httpLink)),
			cache: new InMemoryCache(),
			defaultOptions,
		});
	}
	return client;
};
